import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Grid,
  Card,
  CardContent,
  IconButton,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tooltip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';
import {
  CSS
} from '@dnd-kit/utilities';
import {
  Add,
  DragIndicator,
  Edit,
  Delete,
  Save,
  Preview,
  TextFields,
  Numbers,
  CalendarToday,
  Email,
  Phone,
  CheckBox,
  RadioButtonChecked,
  CloudUpload,
  Draw,
  List as ListIcon,
  Code,
  Visibility,
  Settings
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../../utils/apiConfig';

const FIELD_TYPES = [
  { 
    id: 'text', 
    label: 'Text Input', 
    icon: TextFields, 
    description: 'Single line text input',
    defaultProps: { placeholder: 'Enter text...' }
  },
  { 
    id: 'textarea', 
    label: 'Text Area', 
    icon: TextFields, 
    description: 'Multi-line text input',
    defaultProps: { rows: 4, placeholder: 'Enter description...' }
  },
  { 
    id: 'number', 
    label: 'Number', 
    icon: Numbers, 
    description: 'Numeric input with validation',
    defaultProps: { min: 0, max: 100 }
  },
  { 
    id: 'date', 
    label: 'Date', 
    icon: CalendarToday, 
    description: 'Date picker input'
  },
  { 
    id: 'email', 
    label: 'Email', 
    icon: Email, 
    description: 'Email input with validation'
  },
  { 
    id: 'phone', 
    label: 'Phone', 
    icon: Phone, 
    description: 'Phone number input'
  },
  { 
    id: 'select', 
    label: 'Dropdown', 
    icon: ListIcon, 
    description: 'Single selection dropdown',
    defaultProps: { options: ['Option 1', 'Option 2', 'Option 3'] }
  },
  { 
    id: 'checkbox', 
    label: 'Checkbox', 
    icon: CheckBox, 
    description: 'Single checkbox for yes/no'
  },
  { 
    id: 'radio', 
    label: 'Radio Group', 
    icon: RadioButtonChecked, 
    description: 'Multiple choice radio buttons',
    defaultProps: { options: ['Option 1', 'Option 2', 'Option 3'] }
  },
  { 
    id: 'file', 
    label: 'File Upload', 
    icon: CloudUpload, 
    description: 'File upload field'
  },
  { 
    id: 'signature', 
    label: 'E-Signature', 
    icon: Draw, 
    description: 'Canvas signature capture'
  }
];

const CATEGORIES = [
  'onboarding',
  'compliance', 
  'performance',
  'termination',
  'general'
];

// Draggable field item for the palette
function DraggableFieldType({ fieldType }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: fieldType.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const IconComponent = fieldType.icon;

  return (
    <Card
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      sx={{
        cursor: 'grab',
        '&:hover': { 
          boxShadow: 2,
          transform: 'translateY(-2px)'
        },
        '&:active': {
          cursor: 'grabbing'
        }
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconComponent color="primary" />
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {fieldType.label}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {fieldType.description}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

// Sortable field item in the form canvas
function SortableField({ field, onEdit, onDelete }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const fieldType = FIELD_TYPES.find(ft => ft.id === field.type);
  const IconComponent = fieldType?.icon || TextFields;

  return (
    <Card
      ref={setNodeRef}
      style={style}
      variant="outlined"
      sx={{
        mb: 1,
        '&:hover': { 
          borderColor: 'primary.main',
          boxShadow: 1
        }
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box
            {...attributes}
            {...listeners}
            sx={{ 
              cursor: 'grab',
              display: 'flex',
              alignItems: 'center',
              '&:active': { cursor: 'grabbing' }
            }}
          >
            <DragIndicator color="action" />
          </Box>
          
          <IconComponent color="primary" />
          
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {field.label}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip 
                label={fieldType?.label || field.type} 
                size="small" 
                variant="outlined" 
              />
              {field.required && (
                <Chip 
                  label="Required" 
                  size="small" 
                  color="error" 
                  variant="outlined" 
                />
              )}
            </Stack>
          </Box>

          <Stack direction="row" spacing={1}>
            <IconButton size="small" onClick={() => onEdit(field)}>
              <Edit fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => onDelete(field.id)} color="error">
              <Delete fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

// Field configuration dialog
function FieldConfigDialog({ open, field, onSave, onClose }) {
  const [formData, setFormData] = useState({
    label: '',
    type: 'text',
    required: false,
    placeholder: '',
    description: '',
    options: [],
    validation: {}
  });

  useEffect(() => {
    if (field) {
      setFormData({
        label: field.label || '',
        type: field.type || 'text',
        required: field.required || false,
        placeholder: field.placeholder || '',
        description: field.description || '',
        options: field.options || [],
        validation: field.validation || {}
      });
    }
  }, [field, open]);

  const handleChange = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const handleOptionsChange = (value) => {
    const options = value.split('\n').filter(opt => opt.trim());
    setFormData(prev => ({ ...prev, options }));
  };

  const handleSave = () => {
    const fieldData = {
      ...field,
      ...formData,
      id: field?.id || `field_${Date.now()}`
    };
    onSave(fieldData);
  };

  const fieldType = FIELD_TYPES.find(ft => ft.id === formData.type);
  const needsOptions = ['select', 'radio'].includes(formData.type);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {field?.id ? 'Edit Field' : 'Add Field'}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Field Label"
              value={formData.label}
              onChange={(e) => handleChange('label', e.target.value)}
              required
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Field Type</InputLabel>
              <Select
                value={formData.type}
                onChange={(e) => handleChange('type', e.target.value)}
                label="Field Type"
              >
                {FIELD_TYPES.map(ft => (
                  <MenuItem key={ft.id} value={ft.id}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <ft.icon fontSize="small" />
                      <span>{ft.label}</span>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description/Help Text"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              multiline
              rows={2}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Placeholder Text"
              value={formData.placeholder}
              onChange={(e) => handleChange('placeholder', e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.required}
                  onChange={(e) => handleChange('required', e.target.checked)}
                />
              }
              label="Required Field"
            />
          </Grid>

          {needsOptions && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Options (one per line)"
                value={formData.options.join('\n')}
                onChange={(e) => handleOptionsChange(e.target.value)}
                multiline
                rows={4}
                helperText="Enter each option on a new line"
              />
            </Grid>
          )}

          {formData.type === 'number' && (
            <>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Minimum Value"
                  type="number"
                  value={formData.validation.min || ''}
                  onChange={(e) => handleChange('validation', { 
                    ...formData.validation, 
                    min: e.target.value ? Number(e.target.value) : undefined 
                  })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Maximum Value"
                  type="number"
                  value={formData.validation.max || ''}
                  onChange={(e) => handleChange('validation', { 
                    ...formData.validation, 
                    max: e.target.value ? Number(e.target.value) : undefined 
                  })}
                />
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">Save Field</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function FormBuilderAdvanced() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [formMeta, setFormMeta] = useState({
    name: '',
    slug: '',
    category: 'general',
    description: ''
  });
  
  const [fields, setFields] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [fieldConfigOpen, setFieldConfigOpen] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (!over) {
      setActiveId(null);
      return;
    }

    // If dragging from palette to canvas
    if (FIELD_TYPES.find(ft => ft.id === active.id) && over.id === 'form-canvas') {
      const fieldType = FIELD_TYPES.find(ft => ft.id === active.id);
      const newField = {
        id: `field_${Date.now()}`,
        type: fieldType.id,
        label: `New ${fieldType.label}`,
        required: false,
        placeholder: fieldType.defaultProps?.placeholder || '',
        ...fieldType.defaultProps
      };
      setEditingField(newField);
      setFieldConfigOpen(true);
    }
    
    // If reordering fields in canvas
    if (active.id !== over.id && fields.find(f => f.id === active.id)) {
      const oldIndex = fields.findIndex(f => f.id === active.id);
      const newIndex = fields.findIndex(f => f.id === over.id);
      
      const newFields = [...fields];
      const [movedField] = newFields.splice(oldIndex, 1);
      newFields.splice(newIndex, 0, movedField);
      setFields(newFields);
    }

    setActiveId(null);
  };

  const handleAddField = (fieldData) => {
    if (editingField?.id && fields.find(f => f.id === editingField.id)) {
      // Editing existing field
      setFields(prev => prev.map(f => f.id === editingField.id ? fieldData : f));
    } else {
      // Adding new field
      setFields(prev => [...prev, fieldData]);
    }
    setFieldConfigOpen(false);
    setEditingField(null);
  };

  const handleEditField = (field) => {
    setEditingField(field);
    setFieldConfigOpen(true);
  };

  const handleDeleteField = (fieldId) => {
    setFields(prev => prev.filter(f => f.id !== fieldId));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      if (!formMeta.name || !formMeta.slug) {
        throw new Error('Form name and slug are required');
      }

      if (fields.length === 0) {
        throw new Error('Add at least one field to the form');
      }

      // Build JSON schema
      const schema_json = {
        title: formMeta.name,
        description: formMeta.description,
        type: 'object',
        properties: {},
        required: []
      };

      fields.forEach(field => {
        const property = {
          type: ['number'].includes(field.type) ? 'number' : 'string',
          title: field.label
        };

        if (field.description) {
          property.description = field.description;
        }

        if (field.type === 'date') {
          property.format = 'date';
        } else if (field.type === 'email') {
          property.format = 'email';
        } else if (field.type === 'select' || field.type === 'radio') {
          property.enum = field.options || [];
        }

        if (field.validation) {
          if (field.validation.min !== undefined) property.minimum = field.validation.min;
          if (field.validation.max !== undefined) property.maximum = field.validation.max;
        }

        schema_json.properties[field.id] = property;

        if (field.required) {
          schema_json.required.push(field.id);
        }
      });

      // Create template
      const token = localStorage.getItem('token');
      const resp = await fetch(`${API_URL}/vnext/forms/templates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          name: formMeta.name,
          slug: formMeta.slug,
          category: formMeta.category,
          schema_json
        })
      });

      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Failed to create form');

      setSuccess('Form created successfully!');
      
      setTimeout(() => {
        navigate('/v2/forms');
      }, 1500);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = () => {
    // Simple preview - could be enhanced with modal
    alert('Preview functionality coming soon!');
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Advanced Form Builder
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="outlined"
            startIcon={<Visibility />}
            onClick={handlePreview}
            disabled={fields.length === 0}
          >
            Preview
          </Button>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSave}
            disabled={saving || fields.length === 0}
          >
            {saving ? 'Saving...' : 'Save Form'}
          </Button>
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <Grid container spacing={3}>
          {/* Left Panel - Form Metadata */}
          <Grid item xs={12} lg={3}>
            <Stack spacing={2}>
              {/* Form Details */}
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Form Details
                </Typography>
                <Stack spacing={2}>
                  <TextField
                    fullWidth
                    label="Form Name"
                    value={formMeta.name}
                    onChange={(e) => setFormMeta({ ...formMeta, name: e.target.value })}
                    required
                    size="small"
                  />
                  <TextField
                    fullWidth
                    label="Slug (URL-friendly ID)"
                    value={formMeta.slug}
                    onChange={(e) => setFormMeta({ 
                      ...formMeta, 
                      slug: e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
                    })}
                    required
                    size="small"
                    helperText="e.g., employee-handbook-2025"
                  />
                  <FormControl fullWidth size="small">
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={formMeta.category}
                      label="Category"
                      onChange={(e) => setFormMeta({ ...formMeta, category: e.target.value })}
                    >
                      {CATEGORIES.map(cat => (
                        <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    fullWidth
                    label="Description"
                    value={formMeta.description}
                    onChange={(e) => setFormMeta({ ...formMeta, description: e.target.value })}
                    multiline
                    rows={3}
                    size="small"
                  />
                </Stack>
              </Paper>

              {/* Field Palette */}
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Field Types
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Drag fields to the form canvas
                </Typography>
                <SortableContext 
                  items={FIELD_TYPES.map(ft => ft.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <Stack spacing={1}>
                    {FIELD_TYPES.map(fieldType => (
                      <DraggableFieldType 
                        key={fieldType.id} 
                        fieldType={fieldType} 
                      />
                    ))}
                  </Stack>
                </SortableContext>
              </Paper>
            </Stack>
          </Grid>

          {/* Right Panel - Form Canvas */}
          <Grid item xs={12} lg={9}>
            <Paper 
              id="form-canvas"
              sx={{ 
                p: 3, 
                minHeight: 600,
                border: '2px dashed',
                borderColor: activeId ? 'primary.main' : 'divider',
                bgcolor: activeId ? 'action.hover' : 'background.paper',
                transition: 'all 0.2s'
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                <Typography variant="h6">
                  Form Canvas
                </Typography>
                <Chip 
                  label={`${fields.length} field${fields.length !== 1 ? 's' : ''}`} 
                  size="small" 
                  variant="outlined" 
                />
              </Stack>

              {fields.length === 0 ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 400,
                    color: 'text.secondary'
                  }}
                >
                  <Settings sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
                  <Typography variant="h6" gutterBottom>
                    Start Building Your Form
                  </Typography>
                  <Typography variant="body2" textAlign="center">
                    Drag field types from the left panel to add them to your form.
                    <br />
                    You can reorder fields by dragging them up or down.
                  </Typography>
                </Box>
              ) : (
                <SortableContext 
                  items={fields.map(f => f.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {fields.map(field => (
                    <SortableField
                      key={field.id}
                      field={field}
                      onEdit={handleEditField}
                      onDelete={handleDeleteField}
                    />
                  ))}
                </SortableContext>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Field Configuration Dialog */}
        <FieldConfigDialog
          open={fieldConfigOpen}
          field={editingField}
          onSave={handleAddField}
          onClose={() => {
            setFieldConfigOpen(false);
            setEditingField(null);
          }}
        />

        {/* Drag Overlay */}
        <DragOverlay>
          {activeId ? (
            <Card sx={{ opacity: 0.8 }}>
              <CardContent>
                <Typography variant="body2">
                  {FIELD_TYPES.find(ft => ft.id === activeId)?.label || 
                   fields.find(f => f.id === activeId)?.label}
                </Typography>
              </CardContent>
            </Card>
          ) : null}
        </DragOverlay>
      </Box>
    </DndContext>
  );
}